import { rUniform } from "../../../core/util/Random";
import { MusicalNote } from "../MusicalNote";
import { Instrument } from "./Instrument";
import { createDistortionNode, createNoiseSource } from "./SynthesisUtils";

interface CrashMode {
  frequency: number; // Center frequency of the bandpass filter
  Q: number; // Quality factor of the filter
  decay: number; // Decay time in seconds
  gain?: number; // Gain applied to the filtered signal
  type?: BiquadFilterType; // Filter type (default is bandpass)
}

const crashModes: CrashMode[] = [
  { frequency: 3000, Q: 10, decay: 2.5 }, // low “body” partial, long ring
  { frequency: 3400, Q: 11, decay: 2.2 },
  { frequency: 3900, Q: 12, decay: 2.0 },
  { frequency: 4400, Q: 13, decay: 1.8 },
  { frequency: 5000, Q: 14, decay: 1.6 },
  { frequency: 5700, Q: 15, decay: 1.4 },
  { frequency: 6400, Q: 16, decay: 1.2 },
  { frequency: 7300, Q: 17, decay: 1.0 },
  { frequency: 8300, Q: 18, decay: 0.8 },
  { frequency: 9400, Q: 19, decay: 0.6 },
  { frequency: 10600, Q: 20, decay: 0.5 },
  { frequency: 12100, Q: 22, decay: 0.4 }, // bright sizzle, quick drop-off
  // Sizzle
  { frequency: 8000, Q: 3, decay: 0.1, gain: 2.0, type: "highpass" }, // bright sizzle, quick drop-off
];

/**
 * Crash cymbal instrument using persistent oscillators for realistic metallic crash sounds.
 *
 * Creates a complex crash cymbal sound by combining multiple synthesis layers:
 * - High-frequency filtered noise for the initial "crash" transient
 * - Multiple sine waves at inharmonic frequencies for metallic ring
 * - Long sustain envelope typical of crash cymbals (2-4 seconds)
 *
 * Uses persistent oscillators for better performance and natural voice-stealing behavior.
 */
export class CrashCymbal extends Instrument {
  constructor(audioContext: AudioContext, output: GainNode) {
    super(audioContext, output);

    // Create persistent audio chain
    this.setupPersistentNodes();
    this.initializeParameters();
  }

  /**
   * Set up persistent audio nodes that stay alive for the instrument's lifetime.
   */
  private setupPersistentNodes(): void {}

  /**
   * Play a crash cymbal note using persistent oscillators with click-free voice stealing.
   */
  playNote(note: MusicalNote, startTime: number, _duration: number): void {
    const crashBus = this.audioContext.createGain();

    // and modulate the filter frequency
    const fmOsc = this.audioContext.createOscillator();
    fmOsc.start(startTime);
    fmOsc.type = "sine";
    fmOsc.frequency.value = 80; // carrier at 80 Hz for a metallic shimmer
    const fmGain = this.audioContext.createGain();
    fmGain.gain.value = 300;

    const resonators = crashModes.map((mode) => {
      // White noise
      const noiseSource = createNoiseSource(this.audioContext, { length: 1 });
      noiseSource.start(startTime);

      // But filter it
      const filter = this.audioContext.createBiquadFilter();
      filter.type = mode.type ?? "bandpass";
      filter.frequency.value = mode.frequency;
      filter.Q.value = mode.Q;

      fmOsc.connect(fmGain);
      fmGain.connect(filter.frequency);

      // Envelope
      const gain = this.audioContext.createGain();
      gain.gain.setValueAtTime(mode.gain ?? 1.0, startTime);
      gain.gain.setTargetAtTime(0, startTime, mode.decay * 0.1);

      // Connect nodes
      noiseSource.connect(filter);
      filter.connect(gain);

      return gain;
    });

    const distortion = createDistortionNode(
      this.audioContext,
      (x) => Math.sign(x) * Math.sqrt(Math.abs(x)),
    );

    const comb = this.audioContext.createDelay();
    const fb = this.audioContext.createGain();

    comb.delayTime.value = 0.03; // 30 ms
    fb.gain.value = 0.4;

    for (const resonator of resonators) {
      resonator.connect(crashBus);
    }
    crashBus.connect(comb);
    comb.connect(fb);
    fb.connect(comb);
    comb.connect(distortion);
    distortion.connect(this.gain);
  }

  /**
   * Get valid MIDI notes for crash cymbal.
   */
  getValidNotes(): number[] {
    return [49]; // C#3 crash cymbal note
  }

  /**
   * Initialize crash cymbal parameters.
   */
  private initializeParameters(): void {}
}
